import { useState } from 'react';
import { ApiError } from '@signalvote/api-client';
import { ErrorCode, isStaleWrite, type OpeningBalanceResult } from '@signalvote/shared';
import {
  useClearOpeningBalance,
  useRecordOpeningBalance,
  useUpdateOpeningBalance,
} from '@/hooks/queries/useOpeningBalance';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCampaignTodayString } from '@/lib/calendarUtils';

const CONFLICT_COPY = 'These settings were updated elsewhere. Close this and open it again to see the latest.';

function errorCopy(err: unknown, fallback: string): string {
  if (isStaleWrite(err)) return CONFLICT_COPY;
  return err instanceof ApiError ? err.message : fallback;
}

interface OpeningBalanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing opening balance and date when the campaign already has one. */
  existingBalance?: number | null;
  existingDate?: string | null;
  /** The finance settings token the write is made from. */
  concurrencyVersion: number | null;
  /** Called with the write's result and the token it was sent with. */
  onSaved: (result: OpeningBalanceResult, sentVersion: number | null) => void;
}

export function OpeningBalanceModal({
  open,
  onOpenChange,
  existingBalance,
  existingDate,
  concurrencyVersion,
  onSaved,
}: OpeningBalanceModalProps) {
  const isEdit = existingBalance != null && existingDate != null;
  const today = getCampaignTodayString();
  const [balance, setBalance] = useState(existingBalance?.toFixed(2) ?? '');
  const [date, setDate] = useState(existingDate ?? today);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: record, isPending: recording } = useRecordOpeningBalance();
  const { mutateAsync: update, isPending: updating } = useUpdateOpeningBalance();
  const { mutateAsync: clear, isPending: clearing } = useClearOpeningBalance();

  async function handleSave() {
    setError(null);
    if (balance === '' || Number.isNaN(Number(balance))) {
      setError('Enter a numeric opening balance.');
      return;
    }
    const req = {
      openingBalance: Number(balance),
      openingBalanceDate: date,
      notes: notes || null,
      concurrencyVersion,
    };
    try {
      const result = isEdit ? await update(req) : await record(req);
      onSaved(result, concurrencyVersion);
      onOpenChange(false);
    } catch (err) {
      setError(errorCopy(err, 'Failed to save opening balance.'));
    }
  }

  async function handleClear() {
    setError(null);
    try {
      const result = await clear({ notes: notes || null, concurrencyVersion });
      onSaved(result, concurrencyVersion);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.AccountBalanceOpeningBalanceNotFound) {
        // Idempotent close — nothing to clear.
        onOpenChange(false);
        return;
      }
      setError(errorCopy(err, 'Failed to clear opening balance.'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit opening balance' : 'Record opening balance'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Your cash on hand as of the date you start filing here. It becomes the beginning
          balance of the first report you file in the app. If that report is already generated,
          it keeps its beginning balance; change it on the report instead.
        </p>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="opening-balance">Opening balance (USD)</Label>
            <Input
              id="opening-balance"
              type="number"
              step="0.01"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="opening-balance-date">Opening balance date</Label>
            <Input
              id="opening-balance-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={today}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="opening-balance-notes">Notes (optional)</Label>
            <Input
              id="opening-balance-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Imported from QuickBooks; reviewed by treasurer…"
            />
          </div>

          {error && (
            <div role="alert" className="text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {isEdit && (
            <Button
              variant="ghost"
              type="button"
              onClick={handleClear}
              disabled={clearing}
              className="mr-auto text-destructive"
            >
              {clearing ? 'Clearing…' : 'Clear opening balance'}
            </Button>
          )}
          <Button
            variant="ghost"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={recording || updating || clearing}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={recording || updating}>
            {recording || updating ? 'Saving…' : isEdit ? 'Save changes' : 'Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
